import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getMobileDrawerPaperSx } from '../shared/theme/mobileDrawerOverrides';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationActionHeaderTitleSx,
  getConfigurationAddButtonSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import { getConfigurationRatePlans, getConfigurationRoomTypes, createRatePlan, updateRatePlan, deleteRatePlan, reorderRatePlans } from '../services/api';
import EmptyStateCard from '../components/common/EmptyStateCard';
import RatePlanBoard from '../components/configuration/RatePlanBoard';
import { getEmptyStateTextSx, getEmptyStateTitleSx, resolveDashboardThemeMode } from '../utils/dashboardSurfaceSx';

const DELETE_CONFIRM_WORD = 'confirm';

const isChannelManagerRatePlan = (plan) => (
  (plan?.is_channel_manager_enabled ?? plan?.enableChannelManager ?? true) === true
);

const MEAL_PLAN_OPTIONS = [
  { value: 'RO', label: 'Room Only' },
  { value: 'BB', label: 'Bed & Breakfast' },
  { value: 'HB', label: 'Half Board' },
  { value: 'FB', label: 'Full Board' },
  { value: 'AI', label: 'All Inclusive' },
  { value: 'EP', label: 'European Plan' },
  { value: 'CP', label: 'Continental Plan' },
  { value: 'MAP', label: 'Modified American Plan' },
  { value: 'AP', label: 'American Plan' },
];

const RatePlanPage = () => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [ratePlans, setRatePlans] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingRatePlan, setEditingRatePlan] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ratePlanToDelete, setRatePlanToDelete] = useState(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    meal_plan: '',
    is_pms_enabled: true,
    is_channel_manager_enabled: true,
    is_refundable: true,
    cancellation_policy: '',
    sort_order: 0,
    room_type_ids: [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchRatePlans();
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    if (!openDrawer || !roomTypes.length) return;
    setFormData((prev) => {
      if (prev.room_type_ids?.length) return prev;
      if (isEditMode && editingRatePlan && editingRatePlan.applies_to_all_room_types === false) {
        return prev;
      }
      return { ...prev, room_type_ids: roomTypes.map((roomType) => roomType.id) };
    });
  }, [openDrawer, roomTypes, isEditMode, editingRatePlan]);

  const isDark = currentTheme === 'dark';
  const dangerAccent = '#ef4444';
  const isCmDelete = isChannelManagerRatePlan(ratePlanToDelete);
  const cmDeleteConfirmed = deleteConfirmInput.trim().toLowerCase() === DELETE_CONFIRM_WORD;

  const fetchRatePlans = async () => {
    setLoading(true);
    try {
      const data = await getConfigurationRatePlans();
      setRatePlans(data);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load rate plans. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const data = await getConfigurationRoomTypes();
      setRoomTypes(Array.isArray(data) ? data : []);
    } catch {
      setRoomTypes([]);
    }
  };

  const allRoomTypeIds = roomTypes.map((roomType) => roomType.id);

  const handleOpenDrawer = (plan = null) => {
    // Always reset errors first
    setErrors({});
    
    if (plan) {
      // Edit mode - explicitly set editing plan
      setIsEditMode(true);
      setEditingRatePlan(plan);
      setFormData({
        code: plan.code || '',
        name: plan.name || '',
        description: plan.description || '',
        meal_plan: plan.meal_plan || '',
        is_pms_enabled: plan.is_pms_enabled !== undefined ? plan.is_pms_enabled : true,
        is_channel_manager_enabled:
          plan.is_channel_manager_enabled !== undefined ? plan.is_channel_manager_enabled : true,
        is_refundable: plan.is_refundable !== undefined ? plan.is_refundable : true,
        cancellation_policy: plan.cancellation_policy || '',
        sort_order: plan.sort_order || 0,
        room_type_ids: plan.applies_to_all_room_types || !Array.isArray(plan.room_type_ids)
          ? allRoomTypeIds
          : plan.room_type_ids,
      });
    } else {
      // Create mode - explicitly clear editing plan FIRST to ensure state is reset
      setIsEditMode(false);
      setEditingRatePlan(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        meal_plan: '',
        is_pms_enabled: true,
        is_channel_manager_enabled: true,
        is_refundable: true,
        cancellation_policy: '',
        sort_order: 0,
        room_type_ids: allRoomTypeIds,
      });
    }
    // Open drawer after state updates
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setIsEditMode(false);
    setEditingRatePlan(null);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (
      (field === 'is_pms_enabled' || field === 'is_channel_manager_enabled')
      && errors.applicability
    ) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.applicability;
        return newErrors;
      });
    }
  };

  const toggleRoomType = (roomTypeId) => {
    const selected = new Set(formData.room_type_ids || []);
    if (selected.has(roomTypeId)) {
      selected.delete(roomTypeId);
    } else {
      selected.add(roomTypeId);
    }
    handleInputChange('room_type_ids', Array.from(selected));
  };

  const toggleAllRoomTypes = () => {
    const allSelected = roomTypes.length > 0 && formData.room_type_ids.length === roomTypes.length;
    handleInputChange('room_type_ids', allSelected ? [] : allRoomTypeIds);
  };

  const validateForm = () => {
    const newErrors = {};

    // Code is required for new rate plans, optional for updates
    if (!editingRatePlan) {
      if (!formData.code.trim()) {
        newErrors.code = 'Code is required';
      } else if (formData.code.length > 50) {
        newErrors.code = 'Code must be 50 characters or less';
      }
    } else if (formData.code && formData.code.length > 50) {
      newErrors.code = 'Code must be 50 characters or less';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    if (!formData.is_pms_enabled && !formData.is_channel_manager_enabled) {
      newErrors.applicability = 'Select PMS, Channel Manager, or both.';
    }

    if (roomTypes.length > 0 && (!formData.room_type_ids || formData.room_type_ids.length === 0)) {
      newErrors.room_type_ids = 'Select at least one room type.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors.',
        severity: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      const { sort_order: _sortOrder, ...formFields } = formData;
      const payload = {
        ...formFields,
        is_pms_enabled: Boolean(formData.is_pms_enabled),
        is_channel_manager_enabled: Boolean(formData.is_channel_manager_enabled),
        room_type_ids: formData.room_type_ids || [],
      };
      if (editingRatePlan) {
        await updateRatePlan(editingRatePlan.id, payload);
        setSnackbar({
          open: true,
          message: 'Rate plan updated successfully!',
          severity: 'success',
        });
      } else {
        await createRatePlan(payload);
        setSnackbar({
          open: true,
          message: 'Rate plan created successfully!',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchRatePlans();
    } catch (error) {
      if (error.errors) {
        const {
          non_field_errors: nonFieldErrors,
          ...fieldErrors
        } = error.errors;
        const applicabilityError = Array.isArray(nonFieldErrors)
          ? nonFieldErrors.join(' ')
          : nonFieldErrors;

        setErrors({
          ...fieldErrors,
          ...(applicabilityError ? { applicability: applicabilityError } : {}),
        });
        setSnackbar({
          open: true,
          message: 'Please fix the validation errors.',
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: error.error || `Failed to ${editingRatePlan ? 'update' : 'create'} rate plan. Please try again.`,
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (ids) => {
    const previous = ratePlans;
    setRatePlans((current) => {
      const byId = new Map(current.map((plan) => [plan.id, plan]));
      return ids.map((id, index) => ({ ...byId.get(id), sort_order: index + 1 })).filter((plan) => plan.id);
    });
    setSavingOrder(true);
    try {
      const saved = await reorderRatePlans(ids);
      if (Array.isArray(saved) && saved.length) setRatePlans(saved);
      setSnackbar({ open: true, message: 'Rate plan order saved.', severity: 'success' });
    } catch (error) {
      setRatePlans(previous);
      setSnackbar({
        open: true,
        message: error.error || 'Failed to save rate plan order. Please try again.',
        severity: 'error',
      });
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDeleteClick = (plan) => {
    setRatePlanToDelete(plan);
    setDeleteConfirmInput('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ratePlanToDelete) return;
    if (
      isChannelManagerRatePlan(ratePlanToDelete)
      && deleteConfirmInput.trim().toLowerCase() !== DELETE_CONFIRM_WORD
    ) {
      return;
    }

    setDeletingId(ratePlanToDelete.id);
    try {
      await deleteRatePlan(ratePlanToDelete.id);
      setSnackbar({
        open: true,
        message: 'Rate plan deleted successfully!',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setRatePlanToDelete(null);
      setDeleteConfirmInput('');
      fetchRatePlans();
    } catch (error) {setSnackbar({
        open: true,
        message: error.error || 'Failed to delete rate plan. Please try again.',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRatePlanToDelete(null);
    setDeleteConfirmInput('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading && ratePlans.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
        <Typography
          variant="h6"
          sx={{
            ...getConfigurationActionHeaderTitleSx(isMobileLayout),
            color: isDark ? '#ddd' : '#212121',
          }}
        >
          Rate Plans ({ratePlans.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDrawer(null)}
          sx={getConfigurationAddButtonSx('#4ce1b6', isMobileLayout)}
        >
          Add Rate Plan
        </Button>
      </Box>

      {/* Rate Plans Grid */}
      {ratePlans.length === 0 ? (
        <EmptyStateCard themeMode={currentTheme} dashed sx={{ py: 8, px: 3 }}>
          <DescriptionIcon
            sx={{
              fontSize: 64,
              color: resolveDashboardThemeMode(currentTheme) === 'dark' ? '#64748b' : '#999',
              mb: 2,
              display: 'block',
              mx: 'auto',
            }}
          />
          <Typography variant="h6" sx={{ ...getEmptyStateTitleSx(currentTheme), mb: 1 }}>
            No Rate Plans Found
          </Typography>
          <Typography variant="body2" sx={{ ...getEmptyStateTextSx(currentTheme, 'body2'), mb: 3 }}>
            Get started by creating your first rate plan
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer(null)}
            sx={{
              borderColor: '#4ce1b6',
              color: '#4ce1b6',
              textTransform: 'none',
              '&:hover': {
                borderColor: isDark ? '#3dd1a3' : '#1565c0',
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            Create Rate Plan
          </Button>
        </EmptyStateCard>
      ) : (
        <RatePlanBoard
          ratePlans={ratePlans}
          roomTypes={roomTypes}
          isDark={isDark}
          isMobileLayout={isMobileLayout}
          deletingId={deletingId}
          savingOrder={savingOrder}
          onEdit={handleOpenDrawer}
          onDelete={handleDeleteClick}
          onReorder={handleReorder}
        />
      )}

      {/* Add Rate Plan Drawer — layout matches Room Type drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            ...(isMobileShellLayout() ? getMobileDrawerPaperSx('right') : {}),
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ color: '#4ce1b6', fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                {isEditMode ? 'Edit Rate Plan' : 'Add New Rate Plan'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                color: '#4ce1b6',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.3,
                minWidth: 84,
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                },
                '&.Mui-disabled': {
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#d0d0d0',
                  color: isDark ? '#666' : '#999',
                },
              }}
            >
              Close
            </Button>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <TextField
              label={editingRatePlan ? "Code" : "Code *"}
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              fullWidth
              variant="outlined"
              required={!editingRatePlan}
              error={!!errors.code}
              helperText={errors.code}
              placeholder="e.g., BAR, CORP, NONREF"
              InputProps={{
                startAdornment: <CodeIcon sx={{ mr: 1, color: isDark ? '#999' : '#757575' }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4ce1b6',
                },
              }}
            />
            <TextField
              label="Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              variant="outlined"
              required
              error={!!errors.name}
              helperText={errors.name}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4ce1b6',
                },
              }}
            />
            <TextField
              label="Meal Plan"
              value={formData.meal_plan}
              onChange={(e) => handleInputChange('meal_plan', e.target.value)}
              fullWidth
              select
              variant="outlined"
              InputProps={{
                startAdornment: <RestaurantIcon sx={{ mr: 1, color: isDark ? '#999' : '#757575' }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4ce1b6',
                },
              }}
            >
              <MenuItem value="">None</MenuItem>
              {MEAL_PLAN_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControl
              component="fieldset"
              error={!!errors.room_type_ids}
              fullWidth
              sx={{
                p: 2,
                borderRadius: 1.5,
                border: `1px solid ${
                  errors.room_type_ids
                    ? '#f44336'
                    : isDark
                      ? alpha('#ffffff', 0.12)
                      : '#e0e0e0'
                }`,
                backgroundColor: isDark ? alpha('#ffffff', 0.025) : '#fafafa',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <FormLabel
                  component="legend"
                  sx={{
                    color: errors.room_type_ids ? '#f44336' : isDark ? '#ddd' : '#333',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    '&.Mui-focused': { color: errors.room_type_ids ? '#f44336' : '#4ce1b6' },
                  }}
                >
                  Available for room types
                </FormLabel>
                <Chip
                  size="small"
                  clickable
                  icon={
                    formData.room_type_ids.length === roomTypes.length && roomTypes.length > 0
                      ? <CheckBoxIcon sx={{ fontSize: '16px !important' }} />
                      : <CheckBoxOutlineBlankIcon sx={{ fontSize: '16px !important' }} />
                  }
                  label="Select all"
                  onClick={toggleAllRoomTypes}
                  sx={{
                    height: 26,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    bgcolor: formData.room_type_ids.length === roomTypes.length && roomTypes.length > 0
                      ? alpha('#4ce1b6', isDark ? 0.22 : 0.18)
                      : 'transparent',
                    color: isDark ? '#e2e8f0' : '#334155',
                    border: `1px solid ${alpha('#4ce1b6', 0.35)}`,
                    '& .MuiChip-icon': { color: '#4ce1b6' },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {roomTypes.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b' }}>
                    No room types yet. This rate plan will apply to every room type you add later.
                  </Typography>
                ) : roomTypes.map((roomType) => {
                  const selected = formData.room_type_ids.includes(roomType.id);
                  return (
                    <Chip
                      key={roomType.id}
                      clickable
                      icon={
                        selected
                          ? <CheckBoxIcon sx={{ fontSize: '18px !important' }} />
                          : <CheckBoxOutlineBlankIcon sx={{ fontSize: '18px !important' }} />
                      }
                      label={roomType.name}
                      onClick={() => toggleRoomType(roomType.id)}
                      sx={{
                        height: 34,
                        borderRadius: '999px',
                        fontWeight: 700,
                        px: 0.5,
                        bgcolor: selected
                          ? alpha('#4ce1b6', isDark ? 0.22 : 0.18)
                          : isDark ? alpha('#fff', 0.04) : '#fff',
                        color: selected ? (isDark ? '#4ce1b6' : '#047857') : (isDark ? '#cbd5e1' : '#475569'),
                        border: `1px solid ${selected ? '#4ce1b6' : isDark ? alpha('#fff', 0.12) : '#dbe3ee'}`,
                        boxShadow: selected ? `0 4px 12px ${alpha('#4ce1b6', 0.18)}` : 'none',
                        '& .MuiChip-icon': { color: selected ? '#4ce1b6' : (isDark ? '#94a3b8' : '#94a3b8') },
                        '&:hover': {
                          bgcolor: alpha('#4ce1b6', isDark ? 0.16 : 0.12),
                          borderColor: '#4ce1b6',
                        },
                      }}
                    />
                  );
                })}
              </Box>
              {errors.room_type_ids ? (
                <FormHelperText sx={{ ml: 0, mt: 0.75 }}>{errors.room_type_ids}</FormHelperText>
              ) : (
                <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', mt: 1 }}>
                  All room types are selected by default so existing hotels keep working.
                </Typography>
              )}
            </FormControl>
            <FormControl
              component="fieldset"
              error={!!errors.applicability}
              fullWidth
              sx={{
                p: 2,
                borderRadius: 1.5,
                border: `1px solid ${
                  errors.applicability
                    ? '#f44336'
                    : isDark
                      ? alpha('#ffffff', 0.12)
                      : '#e0e0e0'
                }`,
                backgroundColor: isDark ? alpha('#ffffff', 0.025) : '#fafafa',
              }}
            >
              <FormLabel
                component="legend"
                sx={{
                  color: errors.applicability ? '#f44336' : isDark ? '#ddd' : '#333',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  mb: 0.5,
                  '&.Mui-focused': { color: errors.applicability ? '#f44336' : '#4ce1b6' },
                }}
              >
                Available In
              </FormLabel>
              <FormGroup
                row
                aria-describedby={errors.applicability ? 'rate-plan-applicability-error' : undefined}
                sx={{ columnGap: 2 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_pms_enabled}
                      onChange={(e) => handleInputChange('is_pms_enabled', e.target.checked)}
                      inputProps={{ 'aria-invalid': !!errors.applicability }}
                      sx={{ color: '#4ce1b6', '&.Mui-checked': { color: '#4ce1b6' } }}
                    />
                  }
                  label="PMS"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_channel_manager_enabled}
                      onChange={(e) => handleInputChange('is_channel_manager_enabled', e.target.checked)}
                      inputProps={{ 'aria-invalid': !!errors.applicability }}
                      sx={{ color: '#4ce1b6', '&.Mui-checked': { color: '#4ce1b6' } }}
                    />
                  }
                  label="Channel Manager"
                />
              </FormGroup>
              {errors.applicability && (
                <FormHelperText id="rate-plan-applicability-error" sx={{ ml: 0, mt: 0.5 }}>
                  {errors.applicability}
                </FormHelperText>
              )}
            </FormControl>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4ce1b6',
                },
              }}
            />
            <TextField
              label="Cancellation Policy"
              value={formData.cancellation_policy}
              onChange={(e) => handleInputChange('cancellation_policy', e.target.value)}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4ce1b6',
                      borderWidth: '2px',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#4ce1b6',
                },
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={formData.is_refundable ? 'Refundable' : 'Non-Refundable'}
                onClick={() => handleInputChange('is_refundable', !formData.is_refundable)}
                color={formData.is_refundable ? 'success' : 'error'}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              />
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              pb: isMobileShellLayout() ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
              flexShrink: 0,
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseDrawer}
                  disabled={saving}
                  sx={{
                    borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#ddd' : '#666',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#4ce1b6',
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: '#4ce1b6',
                    },
                    '&:disabled': {
                      borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                      color: isDark ? '#666' : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{
                    backgroundColor: '#4ce1b6',
                    color: '#000',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#4ce1b6',
                      opacity: 0.9,
                      boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                    },
                    '&:disabled': {
                      backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                      color: isDark ? '#999' : '#666',
                    },
                  }}
                >
                  {editingRatePlan ? 'Update Rate Plan' : 'Create Rate Plan'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={deletingId ? undefined : handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: alpha('#0f172a', isDark ? 0.72 : 0.45),
            backdropFilter: 'blur(8px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(dangerAccent, isDark ? 0.4 : 0.22)}`,
            background: isDark
              ? `linear-gradient(165deg, ${alpha(dangerAccent, 0.16)} 0%, #0f172a 42%, #0b1220 100%)`
              : `linear-gradient(165deg, ${alpha(dangerAccent, 0.1)} 0%, #ffffff 46%, #fff7f7 100%)`,
            boxShadow: isDark
              ? `0 24px 60px ${alpha('#000', 0.55)}`
              : `0 22px 50px ${alpha('#7f1d1d', 0.18)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 2.25,
            pb: 1,
            pr: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                backgroundColor: alpha(dangerAccent, isDark ? 0.2 : 0.12),
                border: `1px solid ${alpha(dangerAccent, 0.3)}`,
                boxShadow: `0 8px 18px ${alpha(dangerAccent, 0.18)}`,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 28, color: dangerAccent }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: 0.2, color: isDark ? '#fff' : '#0f172a' }}>
                Delete Rate Plan
              </Typography>
              {ratePlanToDelete ? (
                <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b', mt: 0.25 }} noWrap>
                  {ratePlanToDelete.name} · {ratePlanToDelete.code}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton
            onClick={handleDeleteCancel}
            size="small"
            disabled={deletingId !== null}
            sx={{ color: isDark ? '#94a3b8' : '#64748b' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5, pb: 0.5 }}>
          {isCmDelete ? (
            <>
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(dangerAccent, isDark ? 0.14 : 0.08),
                  border: `1px solid ${alpha(dangerAccent, isDark ? 0.35 : 0.2)}`,
                }}
              >
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: dangerAccent, mb: 0.75 }}>
                  Danger
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.6, color: isDark ? '#fecaca' : '#7f1d1d' }}>
                  Deleting this Channel Manager rate plan can break OTA channel rate sync.
                  Existing mappings and scheduled rate updates may stop working. Contact the
                  support team if you need help before you continue.
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: 1.55 }}>
                Type <Box component="strong" sx={{ color: dangerAccent }}>{DELETE_CONFIRM_WORD}</Box> to permanently delete this plan.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                value={deleteConfirmInput}
                onChange={(event) => setDeleteConfirmInput(event.target.value)}
                placeholder={`Type ${DELETE_CONFIRM_WORD}`}
                error={deleteConfirmInput.length > 0 && !cmDeleteConfirmed}
                helperText={
                  deleteConfirmInput.length > 0 && !cmDeleteConfirmed
                    ? `Enter ${DELETE_CONFIRM_WORD} exactly to enable Delete`
                    : ' '
                }
                sx={{
                  mt: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: isDark ? alpha('#fff', 0.04) : '#fff',
                    '& fieldset': {
                      borderColor: alpha(dangerAccent, cmDeleteConfirmed ? 0.55 : 0.28),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha(dangerAccent, 0.55),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: dangerAccent,
                      borderWidth: 1.5,
                    },
                  },
                }}
              />
            </>
          ) : (
            <Typography sx={{ color: isDark ? '#cbd5e1' : '#475569', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Are you sure you want to delete this rate plan? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.25, pt: 1, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={deletingId !== null}
            sx={{
              minWidth: 100,
              borderRadius: 999,
              fontWeight: 700,
              textTransform: 'none',
              borderColor: alpha(dangerAccent, 0.35),
              color: isDark ? alpha('#fff', 0.85) : '#334155',
              '&:hover': {
                borderColor: dangerAccent,
                backgroundColor: alpha(dangerAccent, 0.06),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deletingId !== null || (isCmDelete && !cmDeleteConfirmed)}
            startIcon={deletingId !== null ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              minWidth: 128,
              borderRadius: 999,
              fontWeight: 800,
              textTransform: 'none',
              backgroundColor: dangerAccent,
              color: '#fff',
              boxShadow: `0 8px 18px ${alpha(dangerAccent, 0.35)}`,
              '&:hover': { backgroundColor: '#dc2626' },
              '&:disabled': {
                backgroundColor: isDark ? alpha('#fff', 0.12) : '#e2e8f0',
                color: isDark ? '#94a3b8' : '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {deletingId !== null ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RatePlanPage;

